/**
 * Le matériel, lu dans `equipment`, `equipment_bulk_items` et leurs voisines
 * (`20261030120000`).
 *
 * La base décide de ce que chacun voit : le terrain ne lit pas `projects`, le
 * nom du chantier lui revient donc nul, et c'est attendu. Les déplacements ne
 * s'écrivent pas dans les tables — ils passent par `move_equipment` et
 * `move_bulk_equipment`, qui tracent le mouvement.
 */
import { DomainError, EQUIPMENT_CATEGORIES } from "@dker/core";
import type {
  BulkItem,
  BulkItemDetails,
  Equipment,
  EquipmentArchiveReason,
  EquipmentCategory,
  EquipmentDetails,
  EquipmentFleet,
  EquipmentLocationKind,
  EquipmentMovement,
  EquipmentRepository,
  ExecutionContext,
} from "@dker/core";
import type { DbClientFactory } from "../ports.js";

/* eslint-disable @typescript-eslint/no-explicit-any */
type Row = Record<string, any>;

const UNIT_COLUMNS =
  "id, name, type_label, category, brand, model, serial_number, ownership, rental_company, " +
  "rental_return_on, location_kind, project_id, located_since, notes, photo_path, archived_at, archive_reason, " +
  "project:projects(name)";

const BULK_COLUMNS =
  "id, name, type_label, category, unit, ownership, rental_company, rental_return_on, " +
  "total_quantity, at_depot, at_repair, notes, archived_at, archive_reason, " +
  "placements:equipment_bulk_placements(project_id, quantity, since, project:projects(name))";

const MOVEMENT_COLUMNS =
  "id, equipment_id, bulk_item_id, quantity, from_kind, from_project_id, to_kind, to_project_id, " +
  "moved_by, moved_at, note, " +
  "from_project:projects!equipment_movements_from_project_id_fkey(name), " +
  "to_project:projects!equipment_movements_to_project_id_fkey(name)";

function fail(error: { message: string; code?: string }): never {
  // Les refus de la base sont écrits pour être lus (« Il n'y en a que 12 à
  // cet endroit. ») : on les rend tels quels.
  if (error.code === "P0001") throw new DomainError("invalid_input", error.message);
  throw new DomainError("conflict", `Opération refusée par la base : ${error.message}`, { pgCode: error.code });
}

const category = (v: unknown): EquipmentCategory =>
  (EQUIPMENT_CATEGORIES as readonly unknown[]).includes(v) ? (v as EquipmentCategory) : "autre";

function toUnit(r: Row): Equipment {
  return {
    id: r.id,
    name: r.name,
    typeLabel: r.type_label,
    category: category(r.category),
    brand: r.brand ?? null,
    model: r.model ?? null,
    serialNumber: r.serial_number ?? null,
    ownership: r.ownership === "rented" ? "rented" : "owned",
    rentalCompany: r.rental_company ?? null,
    rentalReturnOn: r.rental_return_on ?? null,
    locationKind: r.location_kind as EquipmentLocationKind,
    projectId: r.project_id ?? null,
    projectName: r.project?.name ?? null,
    locatedSince: r.located_since,
    notes: r.notes ?? null,
    photoPath: r.photo_path ?? null,
    archivedAt: r.archived_at ?? null,
    archiveReason: (r.archive_reason ?? null) as EquipmentArchiveReason | null,
  };
}

function toBulk(r: Row): BulkItem {
  return {
    id: r.id,
    name: r.name,
    typeLabel: r.type_label,
    category: category(r.category),
    unit: r.unit,
    ownership: r.ownership === "rented" ? "rented" : "owned",
    rentalCompany: r.rental_company ?? null,
    rentalReturnOn: r.rental_return_on ?? null,
    totalQuantity: Number(r.total_quantity),
    atDepot: Number(r.at_depot),
    atRepair: Number(r.at_repair ?? 0),
    notes: r.notes ?? null,
    archivedAt: r.archived_at ?? null,
    archiveReason: (r.archive_reason ?? null) as EquipmentArchiveReason | null,
    placements: ((r.placements ?? []) as Row[]).map((p) => ({
      projectId: p.project_id,
      projectName: p.project?.name ?? null,
      quantity: Number(p.quantity),
      since: p.since,
    })),
  };
}

function toMovement(r: Row): EquipmentMovement {
  return {
    id: r.id,
    equipmentId: r.equipment_id ?? null,
    bulkItemId: r.bulk_item_id ?? null,
    quantity: r.quantity ?? null,
    fromKind: (r.from_kind ?? null) as EquipmentLocationKind | null,
    fromProjectId: r.from_project_id ?? null,
    fromProjectName: r.from_project?.name ?? null,
    toKind: (r.to_kind ?? null) as EquipmentLocationKind | null,
    toProjectId: r.to_project_id ?? null,
    toProjectName: r.to_project?.name ?? null,
    movedBy: r.moved_by ?? null,
    movedAt: r.moved_at,
    note: r.note ?? null,
  };
}

const unitRow = (d: EquipmentDetails) => ({
  name: d.name,
  type_label: d.typeLabel,
  category: d.category,
  brand: d.brand ?? null,
  model: d.model ?? null,
  serial_number: d.serialNumber ?? null,
  ownership: d.ownership,
  rental_company: d.rentalCompany ?? null,
  rental_return_on: d.rentalReturnOn ?? null,
  notes: d.notes ?? null,
});

const bulkRow = (d: BulkItemDetails) => ({
  name: d.name,
  type_label: d.typeLabel,
  category: d.category,
  unit: d.unit,
  ownership: d.ownership,
  rental_company: d.rentalCompany ?? null,
  rental_return_on: d.rentalReturnOn ?? null,
  total_quantity: d.totalQuantity,
  notes: d.notes ?? null,
});

export function createSupabaseEquipmentRepository(clientFor: DbClientFactory): EquipmentRepository {
  return {
    async list(ctx: ExecutionContext, includeArchived: boolean): Promise<EquipmentFleet> {
      const db = clientFor(ctx);
      let units = db.from("equipment").select(UNIT_COLUMNS).order("name");
      let bulk = db.from("equipment_bulk_items").select(BULK_COLUMNS).order("name");
      if (!includeArchived) {
        units = units.is("archived_at", null);
        bulk = bulk.is("archived_at", null);
      }
      const [u, b] = await Promise.all([units, bulk]);
      if (u.error) fail(u.error);
      if (b.error) fail(b.error);
      return {
        units: ((u.data ?? []) as Row[]).map(toUnit),
        bulk: ((b.data ?? []) as Row[]).map(toBulk),
      };
    },

    async history(ctx, filter): Promise<EquipmentMovement[]> {
      let q = clientFor(ctx).from("equipment_movements").select(MOVEMENT_COLUMNS);
      if (filter.equipmentId) q = q.eq("equipment_id", filter.equipmentId);
      if (filter.bulkItemId) q = q.eq("bulk_item_id", filter.bulkItemId);
      const { data, error } = await q.order("moved_at", { ascending: false }).limit(100);
      if (error) fail(error);
      return ((data ?? []) as Row[]).map(toMovement);
    },

    async add(ctx, details, projectId) {
      const { data, error } = await clientFor(ctx)
        .from("equipment")
        .insert({
          ...unitRow(details),
          location_kind: projectId ? "chantier" : "depot",
          project_id: projectId,
        })
        .select("id")
        .single();
      if (error) fail(error);
      return { id: (data as Row).id };
    },

    async update(ctx, id, details) {
      const { error } = await clientFor(ctx).from("equipment").update(unitRow(details)).eq("id", id);
      if (error) fail(error);
    },

    async setArchived(ctx, id, reason) {
      const { error } = await clientFor(ctx)
        .from("equipment")
        .update(
          reason
            ? { archived_at: new Date().toISOString(), archive_reason: reason }
            : { archived_at: null, archive_reason: null },
        )
        .eq("id", id);
      if (error) fail(error);
    },

    async setPhoto(ctx, id, photoPath) {
      const { error } = await clientFor(ctx).from("equipment").update({ photo_path: photoPath }).eq("id", id);
      if (error) fail(error);
    },

    async move(ctx, id, to, note) {
      const { error } = await clientFor(ctx).rpc("move_equipment", {
        p_equipment_id: id,
        p_to_kind: to.kind,
        p_to_project: to.projectId,
        p_note: note,
      });
      if (error) fail(error);
    },

    async addBulk(ctx, details) {
      const { data, error } = await clientFor(ctx)
        .from("equipment_bulk_items")
        .insert(bulkRow(details))
        .select("id")
        .single();
      if (error) fail(error);
      return { id: (data as Row).id };
    },

    async updateBulk(ctx, id, details) {
      const { error } = await clientFor(ctx).from("equipment_bulk_items").update(bulkRow(details)).eq("id", id);
      if (error) fail(error);
    },

    async setBulkArchived(ctx, id, reason) {
      const { error } = await clientFor(ctx)
        .from("equipment_bulk_items")
        .update(
          reason
            ? { archived_at: new Date().toISOString(), archive_reason: reason }
            : { archived_at: null, archive_reason: null },
        )
        .eq("id", id);
      if (error) fail(error);
    },

    async moveBulk(ctx, id, move, note) {
      const { error } = await clientFor(ctx).rpc("move_bulk_equipment", {
        p_item_id: id,
        p_from_kind: move.from.kind,
        p_from_project: move.from.projectId,
        p_to_kind: move.to.kind,
        p_to_project: move.to.projectId,
        p_quantity: move.quantity,
        p_note: note,
      });
      if (error) fail(error);
    },
  };
}
